import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import {AnnouncementFormValues} from '../types';
import {SVG} from '../../../../../../constants/assetLists';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import AppButton from '../../../../../../components/common/AppButton';
import {useQuery} from '@tanstack/react-query';
import {handleGetAllEquipmentsService} from '../../../../../../services/operations/equipments';
import QUERY_KEYS from '../../../../../../constants/queryKeys';
import {useMemo, useEffect} from 'react';

// Define equipment interface based on actual API response structure
interface Equipment {
  _id: string;
  name: string;
  description: string;
  files: Array<{
    url: string;
    type: string;
  }>;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

// API response structure
interface EquipmentResponse {
  equipments: Equipment[];
  total: number;
  page: string;
  limit: string;
}

type Props = {
  forForm: 'announcement' | 'task';
  index?: number;
  handleExpandSection: (section: string) => void;
  expandedSections: string[];
};

function Equipment({forForm, index, handleExpandSection, expandedSections}: Props) {
  const {
    control,
    setValue,
    trigger,
    formState: {errors},
    watch,
  } = useFormContext<AnnouncementFormValues>();

  const isExpanded =
    (forForm == 'announcement' && expandedSections.includes('equipment')) ||
    (forForm == 'task' && expandedSections.includes(`task-equipment-${index}`));

  const equipmentFieldName =
    forForm === 'task'
      ? (`tasks.${index}.equipments` as keyof AnnouncementFormValues)
      : 'equipments';
  const equipments = watch(equipmentFieldName);

  const {fields, append, remove} = useFieldArray<
    AnnouncementFormValues,
    any,
    'id'
  >({
    control,
    name: equipmentFieldName,
  });

  // Fetch equipment data using React Query
  const {data: equipmentResponse} = useQuery<EquipmentResponse>({
    queryKey: [QUERY_KEYS.GET_ALL_EQUIPMENTS],
    queryFn: handleGetAllEquipmentsService,
  });

  // Transform equipment data for autocomplete component
  const equipmentOptions = useMemo(() => {
    const list = equipmentResponse?.equipments ?? [];
    return list.map(eq => ({value: eq._id, title: eq.name}));
  }, [equipmentResponse?.equipments]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-start">
        <div
          className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity duration-200"
          onClick={() =>
            handleExpandSection(
              forForm === 'announcement'
                ? 'equipment'
                : `task-equipment-${index}`,
            )
          }>
          <div className="w-[24px] h-[24px]">
            <img
              src={SVG.equipmentIcon}
              alt="icon"
              className="w-[20px] h-[19px] object-contain"
            />
          </div>
          <p className="m-0 text-[20px] font-medium text-black">שיוך ציוד</p>
        </div>
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-3">
          {fields?.map((field, ind) => {
            return (
              <Controller
                key={field.id}
                name={
                  forForm === 'task'
                    ? (`tasks.${index}.equipments.${ind}` as keyof AnnouncementFormValues)
                    : `equipments.${ind}`
                }
                control={control}
                render={({field}) => {
                  const options = equipmentOptions?.filter(
                    eq =>
                      !(equipments as {value: string}[]).some(
                        f => f.value === eq.value,
                      ),
                  );
                  const value: any =
                    (field.value as {value: string}).value || '';
                  const selectedOption =
                    equipmentResponse?.equipments?.find(
                      item => item._id === value,
                    ) || null;

                  return (
                    <div className="flex flex-wrap items-center gap-4">
                      <AppMUIAutocomplete
                        options={options || []}
                        sx={{width: 317}}
                        getOptionLabel={option => option.title}
                        value={
                          equipmentOptions.find(opt => opt.value === value) ||
                          null
                        }
                        onChange={(_, newValue) => {
                          const previousValue = (field.value as {value: string})
                            .value;

                          setValue(
                            forForm === 'task'
                              ? (`tasks.${index}.equipments.${ind}` as keyof AnnouncementFormValues)
                              : `equipments.${ind}`,
                            {
                              value: newValue?.value || '',
                            },
                            {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            },
                          );

                          // If clearing a field that had a value, remove it (but keep at least one field)
                          if (previousValue && !newValue?.value) {
                            const currentEquipments = equipments as {
                              value: string;
                            }[];
                            if (currentEquipments.length > 1) {
                              remove(ind);
                            }
                          }
                        }}
                        label="רשימת ציוד לבחירה"
                        size="small"
                      />

                      {selectedOption && selectedOption?.files?.length > 0 ? (
                        <div className="flex items-center gap-1">
                          <div className="w-[20px] h-[18px]">
                            <img
                              src={SVG.equipmentCardIcon}
                              alt="icon"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <button className="text-darkBlue underline">
                            כרטיס ציוד
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                }}
              />
            );
          })}

          {/* Show "Add Equipment" button only when at least one equipment is selected */}
          {(equipments as {value: string}[])?.some(eq => eq.value) && (
            <div>
              <AppButton
                iconStart={
                  <div className="w-[12px] h-[12px]">
                    <img
                      src={SVG.addIcon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                }
                onClick={() => append({value: ''})}>
                שיוך ציוד להודעה
              </AppButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Equipment;
